import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthId } from '@/hooks/useAuthId';
import { useSession } from '@/lib/auth';
import { sessionRequests } from '@/lib/auth/sessionRequests';
import { socialLeaderboardKeys } from '@/lib/domain/socialLeaderboards';

export function useBoardInvite(inviteSlug?: string | null) {
  return useQuery({
    queryKey: [...socialLeaderboardKeys.all, 'boardInvite', inviteSlug] as const,
    queryFn: async () => {
      const { resolveBoardInvite } = await import('@/lib/services/socialLeaderboards');
      return resolveBoardInvite(inviteSlug!);
    },
    enabled: !!inviteSlug,
    staleTime: 1000 * 60 * 5,
  });
}

interface BoardJoinAttempt {
  key: string;
  authRevision: number;
  retiredBeforeDispatch: boolean;
  input: { boardId: string; userId: string; isAnonymous: boolean };
}

export function useJoinBoardFromInvite(inviteSlug?: string | null) {
  const queryClient = useQueryClient();
  const { userId, isAuthenticated, isResolved } = useAuthId();
  const { authRevision = 0 } = useSession();
  const isAnonymous = !isAuthenticated;
  const inviteQuery = useBoardInvite(inviteSlug);
  const lastAttempt = useRef<BoardJoinAttempt | null>(null);
  const boardId = inviteQuery.data?.board_id;

  const { mutate, isPending, error } = useMutation({
    mutationFn: async (attempt: BoardJoinAttempt) => {
      const assertOwner = sessionRequests.captureOwner();
      const { joinBoard } = await import('@/lib/services/socialLeaderboards');
      try {
        assertOwner();
      } catch (error) {
        // This intent has not dispatched. Only a newer ready session may retry it.
        attempt.retiredBeforeDispatch = true;
        throw error;
      }
      return joinBoard(attempt.input);
    },
    onSuccess: (_board, attempt) => {
      queryClient.invalidateQueries({
        queryKey: socialLeaderboardKeys.boards(attempt.input.userId, attempt.input.isAnonymous),
      });
      queryClient.invalidateQueries({ queryKey: socialLeaderboardKeys.all });
    },
  });

  useEffect(() => {
    if (!inviteSlug || !isResolved || !userId || !boardId || isPending) return;
    const key = `${boardId}:${userId}:${isAnonymous ? 'anon' : 'auth'}`;
    const previous = lastAttempt.current;
    if (
      previous?.key === key &&
      (!previous.retiredBeforeDispatch || previous.authRevision === authRevision)
    )
      return;
    const attempt: BoardJoinAttempt = {
      key,
      authRevision,
      retiredBeforeDispatch: false,
      input: { boardId, userId, isAnonymous },
    };
    lastAttempt.current = attempt;
    mutate(attempt);
  }, [inviteSlug, isResolved, userId, isAnonymous, boardId, authRevision, isPending, mutate]);

  return {
    board: inviteQuery.data ?? null,
    isLoading: inviteQuery.isLoading || isPending,
    error: inviteQuery.error ?? error,
  };
}
